import React, { useEffect, useState } from "react";
import ProductAddToCart from "./ProductAddToCart";
import { globalFileServer, globalFileIcon } from "../../../../axiosInstance";
import ProductInfo from "../../productInfo/ProductInfo";
import PricesCompareTable from "./PricesCompareTable";
import { getDefaultMediaPath } from "../../../utils/productMedia";
import {
  getProductImageUrl,
  getResizedProductImageUrl,
  getWebpVariantUrl,
} from "../../../utils/productImageLoader";

const ProductPage = ({
  selectedProd,
  setImageModal,
  alwaysShowBrand = false,
}) => {
  const [_currSlide, setCurrSlide] = useState(1);
  const initialImagePath = getDefaultMediaPath(selectedProd?.media);
  const [imgView, setImgView] = useState(initialImagePath || false);
  /** 0 = webp+600, 1 = 600 jpeg/png only, 2 = original (fallback ladder). */
  const [imgStage, setImgStage] = useState(0);

  // The popup instance can be reused across products without a remount —
  // re-derive the image and restart the fallback ladder when that happens.
  useEffect(() => {
    setImgView(getDefaultMediaPath(selectedProd?.media) || false);
    setImgStage(0);
  }, [selectedProd]);

  // The dialog shows the image at ~500px — the 600 variant is ideal; the
  // full-size original is only needed for the zoom modal.
  const popupOriginal = imgView
    ? getProductImageUrl(imgView)
    : `${globalFileServer}/placeholder.jpg`;
  const popupSrc =
    imgView && imgStage < 2
      ? getResizedProductImageUrl(imgView, 600)
      : popupOriginal;
  const popupWebp =
    imgView && imgStage === 0 ? getWebpVariantUrl(imgView, 600) : undefined;
  // The decorative backdrop shares the img's URL so webp-capable browsers
  // fetch ONE file for both marks instead of webp + jpeg of the same image.
  const popupBackdrop = popupWebp || popupSrc;
  const handlePopupImgError = (event) => {
    // A failed webp source drops to the jpeg variant; anything else jumps
    // straight to the original — re-presenting an identical failed URL
    // never re-fetches and would stall the ladder.
    const failedUrl = event?.currentTarget?.currentSrc || "";
    setImgStage((stage) =>
      Math.min(Math.max(failedUrl.endsWith(".webp") ? 1 : 2, stage + 1), 2),
    );
  };

  const _settings = {
    rtl: window.innerWidth > 1000 ? true : false,
    dots: false,
    arrows: window.innerWidth > 1000 ? true : false,
    infinite: window.innerWidth > 1000 ? true : false,
    speed: 200,
    slidesToShow:
      window.innerWidth > 1000
        ? selectedProd.PathUrl && selectedProd.PathUrl.split(",").length < 3
          ? selectedProd.PathUrl.split(",").length
          : 3
        : 1,
    slidesToScroll:
      window.innerWidth > 1000
        ? selectedProd.PathUrl && selectedProd.PathUrl.split(",").length < 3
          ? selectedProd.PathUrl.split(",").length
          : 3
        : 1,
    afterChange: (index) => setCurrSlide(index + 1),
  };

  return (
    <div className="product-pop-up">
      <div className="product-img">
        <picture>
          {popupWebp ? <source type="image/webp" srcSet={popupWebp} /> : null}
          <img
            loading="lazy"
            decoding="async"
            alt=""
            onClick={() => setImageModal(imgView)}
            src={popupSrc}
            onError={handlePopupImgError}
          />
        </picture>
        <div
          onClick={() => setImageModal(imgView)}
          className="img"
          style={{
            backgroundImage: imgView
              ? `url(${popupBackdrop})`
              : `url(${globalFileServer}/placeholder.jpg)`,
          }}
        />
        <div className="price-btn">
          <ProductAddToCart
            element={selectedProd}
            haveInfo={false}
            retailCard
          />
        </div>
      </div>

      <div className="product-details">
        <div
          className="prod-info"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div style={{ flex: 1 }}>
            <ProductInfo
              selectedProd={selectedProd}
              alwaysShowBrand={alwaysShowBrand}
            />
            <div className="prod-info-quantity">
              {selectedProd.weightJump > 1 ? (
                <span>
                  <br />
                  <span>{selectedProd.weightJump}</span>
                  <span> יחי' של </span>
                  <span className="weight-quantity">
                    {selectedProd.weightDetails?.netContent}
                  </span>
                  <span>{selectedProd.weightDetails?.unitOfMeasure}</span>
                </span>
              ) : null}
            </div>
          </div>
          <div className="kashrut">
            {selectedProd.kosherSupervision?.[0]?.supervisionAgency?.img
              ?.path &&
            selectedProd.kosherSupervision[0].supervisionAgency.img.path !==
              "19.svg" ? (
              <img
                loading="lazy"
                src={
                  globalFileIcon +
                  selectedProd.kosherSupervision[0].supervisionAgency.img.path
                }
                alt={
                  selectedProd.kosherSupervision[0].supervisionAgency.name ||
                  "כשרות"
                }
              />
            ) : null}
          </div>
        </div>
        <div>
          {selectedProd.gtin && (
            <p>{selectedProd.gtin && `ברקוד: ${selectedProd.gtin}`}</p>
          )}
        </div>
        <PricesCompareTable
          selectedProd={selectedProd}
          mobileAction={
            <div className="price-btn price-btn--mobile">
              <ProductAddToCart
                element={selectedProd}
                haveInfo={false}
                retailCard
              />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default ProductPage;
